import React from 'react';
import { Route, Switch } from 'react-router-dom';
import asyncComponent from './components/AsyncComponent';
import AppliedRoute from './components/AppliedRoute';
import AuthenticatedRoute from './components/AuthenticatedRoute';
import UnauthenticatedRoute from './components/UnauthenticatedRoute';
import PeriodicBillsDueList from './containers/PeriodicBillsDueList';

const Home = asyncComponent(() => import('./containers/Home'));
const Login = asyncComponent(() => import('./containers/Login'));
const ForgotPassword = asyncComponent(() => import('./containers/ForgotPassword'));
const ResetForgottenPassword = asyncComponent(() => import('./containers/ResetForgottenPassword'));
const NotFound = asyncComponent(() => import('./containers/NotFound'));
const ImportMeters = asyncComponent(() => import('./containers/ImportMeters'));
const ImportPeople = asyncComponent(() => import('./containers/ImportPeople'));
const IssueList = asyncComponent(() => import('./containers/IssueList'));
const IssueDetails = asyncComponent(() => import('./containers/IssueDetails'));
const ImportProperties = asyncComponent(() => import('./containers/ImportProperty'));
const TariffList = asyncComponent(() => import('./containers/TariffList'));
const TariffDetails = asyncComponent(() => import('./containers/TariffDetails'));
const TariffChange = asyncComponent(() => import('./containers/TariffChange'));
const ManagingAgentCreate = asyncComponent(() => import('./containers/ManagingAgentCreate'));
const ManagingAgentList = asyncComponent(() => import('./containers/ManagingAgentList'));
const ManagingAgentDetails = asyncComponent(() => import('./containers/ManagingAgentDetails'));
const ManagingAgentEdit = asyncComponent(() => import('./containers/ManagingAgentEdit'));
const ManagingAgentMove = asyncComponent(() => import('./containers/ManagingAgentMove'));
const ManagementAgreementList = asyncComponent(() =>
  import('./containers/ManagementAgreementList'),
);
const ManagementAgreementDetails = asyncComponent(() =>
  import('./containers/ManagementAgreementDetails'),
);
const MeterList = asyncComponent(() => import('./containers/MeterList'));
const MeterDetails = asyncComponent(() => import('./containers/MeterDetails'));
const CommissionMeter = asyncComponent(() =>
  import('./containers/meter_containers/CommissionMeter'),
);
const DeCommissionMeter = asyncComponent(() =>
  import('./containers/meter_containers/DeCommissionMeter'),
);
const MeterCreate = asyncComponent(() => import('./containers/MeterCreate'));
const DevelopmentCreate = asyncComponent(() => import('./containers/DevelopmentCreate'));
const DevelopmentList = asyncComponent(() => import('./containers/DevelopmentList'));
const DevelopmentDetails = asyncComponent(() => import('./containers/DevelopmentDetails'));
const BuildingCreate = asyncComponent(() => import('./containers/BuildingCreate'));
const BuildingList = asyncComponent(() => import('./containers/BuildingList'));
const BuildingDetails = asyncComponent(() => import('./containers/BuildingDetails'));
const ApartmentList = asyncComponent(() => import('./containers/ApartmentList'));
const ApartmentDetails = asyncComponent(() => import('./containers/ApartmentDetails'));
const OccupantMove = asyncComponent(() => import('./containers/OccupantMove'));
const OccupantList = asyncComponent(() => import('./containers/OccupantList'));
const OccupantDetails = asyncComponent(() => import('./containers/OccupantDetails'));
const LesseeList = asyncComponent(() => import('./containers/LesseeList'));
const LesseeDetails = asyncComponent(() => import('./containers/LesseeDetails'));
const LesseeMove = asyncComponent(() => import('./containers/LesseeMove'));
const LeaseList = asyncComponent(() => import('./containers/LeaseList'));
const LeaseDetails = asyncComponent(() => import('./containers/LeaseDetails'));
const SubLeaseList = asyncComponent(() => import('./containers/SubLeaseList'));
const SubLeaseDetails = asyncComponent(() => import('./containers/SubLeaseDetails'));
const BillList = asyncComponent(() => import('./containers/BillList'));
const BillDetails = asyncComponent(() => import('./containers/BillDetails'));
const CreditNoteList = asyncComponent(() => import('./containers/CreditNoteList'));
const BillingApprovalReportList = asyncComponent(() =>
  import('./containers/BillingApprovalReportList'),
);
const BillingApprovalReportDetails = asyncComponent(() =>
  import('./containers/BillingApprovalReportDetails'),
);
const ManagementAgreementEdit = asyncComponent(() =>
  import('./containers/ManagementAgreementEdit'),
);

/**
 * AppliedRoute: Can be logged in and logged out as well
 * UnauthenticatedRoute: Only logged out
 * AuthenticatedRoute: Only logged in
 *                     Optional `groups` attr. Empty/undefined means any group
 */

export default ({ childProps }) => (
  <Switch>
    <AppliedRoute path="/" exact component={Home} props={childProps} title="Home" />
    <UnauthenticatedRoute
      path="/login/:email?"
      exact
      component={Login}
      props={childProps}
      title="Login"
    />
    <UnauthenticatedRoute
      path="/forgot-password/:email?"
      component={ForgotPassword}
      props={childProps}
      title="Forgot password"
    />
    <UnauthenticatedRoute
      path="/reset-forgotten-password/:email/:token"
      exact
      component={ResetForgottenPassword}
      props={childProps}
      title="Reset password"
    />
    <AuthenticatedRoute
      path="/issues"
      title="Issues"
      exact
      component={IssueList}
      props={childProps}
      highlight="nonActionableContentColor"
    />
    <AuthenticatedRoute
      path="/issues/:issueID"
      title="View issue"
      exact
      component={IssueDetails}
      props={childProps}
      highlight="actionableContentColor"
    />
    <AuthenticatedRoute
      path="/import/property/apartments"
      exact
      component={ImportProperties}
      props={childProps}
      title="Import apartments"
      highlight="actionableContentColor"
    />
    <AuthenticatedRoute
      path="/tariffs"
      exact
      component={TariffList}
      title="Tariffs"
      props={childProps}
      highlight="nonActionableContentColor"
    />
    <AuthenticatedRoute
      path="/tariffs/create"
      exact
      component={TariffDetails}
      title="Create tariff"
      props={childProps}
    />
    <AuthenticatedRoute
      path="/tariffs/change"
      exact
      component={TariffChange}
      title="Change tariff"
      props={childProps}
      highlight="actionableContentColor"
      groups={['all', 'tariff_actions']}
    />
    <AuthenticatedRoute
      path="/tariffs/:tariffID"
      exact
      component={TariffDetails}
      props={childProps}
      title="View tariff"
    />
    <AuthenticatedRoute
      path="/managing_agents/create"
      exact
      component={ManagingAgentCreate}
      props={childProps}
      title="Create managing agent"
      highlight="actionableContentColor"
    />
    <AuthenticatedRoute
      path="/managing_agents"
      exact
      component={ManagingAgentList}
      props={childProps}
      title="Managing agents"
      highlight="nonActionableContentColor"
    />
    <AuthenticatedRoute
      path="/managing_agents/move"
      exact
      component={ManagingAgentMove}
      props={childProps}
      title="Move managing agents"
      highlight="actionableContentColor"
    />
    <AuthenticatedRoute
      path="/managing_agents/:managingAgentID"
      exact
      component={ManagingAgentDetails}
      props={childProps}
      title="View managing agent"
    />
    <AuthenticatedRoute
      path="/managing_agents/:managingAgentID/edit"
      exact
      component={ManagingAgentEdit}
      props={childProps}
      title="Edit managing agent"
      highlight="actionableContentColor"
    />
    <AuthenticatedRoute
      path="/meter_and_billing_agreements"
      exact
      component={ManagementAgreementList}
      props={childProps}
      title="Managing agreements"
      highlight="nonActionableContentColor"
    />
    <AuthenticatedRoute
      path="/meter_and_billing_agreements/:managementAgreementID"
      exact
      component={ManagementAgreementDetails}
      props={childProps}
      title="View managing agreement"
    />
    <AuthenticatedRoute
      path="/meters"
      exact
      component={MeterList}
      props={childProps}
      title="Meters"
      highlight="nonActionableContentColor"
    />
    <AuthenticatedRoute
      path="/meters/create"
      exact
      component={MeterCreate}
      props={childProps}
      title="Create meter"
      highlight="actionableContentColor"
    />
    <AuthenticatedRoute
      path="/meters/:meterID/commission"
      exact
      component={CommissionMeter}
      props={childProps}
      title="Commission meter"
    />
    <AuthenticatedRoute
      path="/meters/:meterID/decommission"
      exact
      component={DeCommissionMeter}
      props={childProps}
      title="Decommission meter"
    />
    <AuthenticatedRoute
      path="/meters/:meterID"
      exact
      component={MeterDetails}
      props={childProps}
      title="View meter"
    />
    <AuthenticatedRoute
      path="/import/people"
      exact
      component={ImportPeople}
      props={childProps}
      title="Import people"
      highlight="actionableContentColor"
    />
    <AuthenticatedRoute
      path="/import/meters"
      exact
      component={ImportMeters}
      props={childProps}
      title="Import meter"
      highlight="actionableContentColor"
    />
    <AuthenticatedRoute
      path="/import/property/development"
      exact
      component={DevelopmentCreate}
      props={childProps}
      title="Create development"
      highlight="actionableContentColor"
    />
    <AuthenticatedRoute
      path="/developments"
      exact
      component={DevelopmentList}
      props={childProps}
      title="Developments"
      highlight="nonActionableContentColor"
    />
    <AuthenticatedRoute
      path="/developments/:developmentID"
      exact
      component={DevelopmentDetails}
      props={childProps}
      title="View development"
    />
    <AuthenticatedRoute
      path="/import/property/building"
      exact
      component={BuildingCreate}
      props={childProps}
      title="Create building"
      highlight="actionableContentColor"
    />
    <AuthenticatedRoute
      path="/buildings"
      exact
      component={BuildingList}
      props={childProps}
      title="Buildings"
      highlight="nonActionableContentColor"
    />
    <AuthenticatedRoute
      path="/buildings/:buildingID"
      exact
      component={BuildingDetails}
      props={childProps}
      title="View building"
    />
    <AuthenticatedRoute
      path="/apartments"
      exact
      component={ApartmentList}
      props={childProps}
      title="Apartments"
      highlight="nonActionableContentColor"
    />
    <AuthenticatedRoute
      path="/apartments/:apartmentID"
      exact
      component={ApartmentDetails}
      props={childProps}
      title="View apartment"
    />
    <AuthenticatedRoute
      path="/occupants"
      exact
      component={OccupantList}
      props={childProps}
      title="Occupants"
      highlight="nonActionableContentColor"
    />
    <AuthenticatedRoute
      path="/occupants/move"
      exact
      component={OccupantMove}
      props={childProps}
      title="Move occupant"
      highlight="actionableContentColor"
    />
    <AuthenticatedRoute
      path="/occupants/:occupantID"
      exact
      component={OccupantDetails}
      props={childProps}
      title="View occupant"
    />
    <AuthenticatedRoute
      path="/lessees"
      exact
      component={LesseeList}
      props={childProps}
      title="Lessees"
      highlight="nonActionableContentColor"
    />
    <AuthenticatedRoute
      path="/lessees/move"
      exact
      component={LesseeMove}
      props={childProps}
      title="Move lessee"
      highlight="actionableContentColor"
    />
    <AuthenticatedRoute
      path="/lessees/:lesseeID"
      exact
      component={LesseeDetails}
      props={childProps}
      title="View lessee"
    />
    <AuthenticatedRoute
      path="/leases"
      exact
      component={LeaseList}
      props={childProps}
      title="Leases"
      highlight="nonActionableContentColor"
    />
    <AuthenticatedRoute
      path="/leases/:leaseID"
      exact
      component={LeaseDetails}
      props={childProps}
      title="View lease"
    />
    <AuthenticatedRoute
      path="/subleases"
      exact
      component={SubLeaseList}
      props={childProps}
      title="Sub leases"
      highlight="nonActionableContentColor"
    />
    <AuthenticatedRoute
      path="/subleases/:subLeaseID"
      exact
      component={SubLeaseDetails}
      props={childProps}
      title="View sub lease"
    />
    <AuthenticatedRoute
      path="/bills"
      exact
      component={BillList}
      props={childProps}
      title="Bills"
      highlight="nonActionableContentColor"
    />
    <AuthenticatedRoute
      path="/bills/:billID"
      exact
      component={BillDetails}
      props={childProps}
      title="View bill"
    />
    <AuthenticatedRoute
      path="/periodic-bills-due"
      exact
      component={PeriodicBillsDueList}
      props={childProps}
      title="Due periodic bills"
      groups={['all', 'billing_actions']}
    />
    <AuthenticatedRoute
      path="/credit_notes"
      exact
      component={CreditNoteList}
      props={childProps}
      highlight="nonActionableContentColor"
      title="Credit notes"
    />
    <AuthenticatedRoute
      path="/billing-approval-reports"
      exact
      component={BillingApprovalReportList}
      props={childProps}
      title="Billing approval reports"
    />
    <AuthenticatedRoute
      path="/billing-approval-reports/:billingApprovalReportID"
      exact
      component={BillingApprovalReportDetails}
      props={childProps}
      title="View billing approval report"
    />
    <AuthenticatedRoute
      path="/management-agreements/:id/edit"
      exact
      component={ManagementAgreementEdit}
      props={childProps}
      title="Edit management agreement"
    />
    {/* Finally, catch all unmatched routes */}
    <Route component={NotFound} title="Page not found" />
  </Switch>
);



// WEBPACK FOOTER //
// ./src/Routes.js